@if (vm.claimType === 'repair') {
  @if (vm.isAD && vm.isBD) {
    <h2 class="heading-4" data-testid="repair-heading">What happened?</h2>
    @if (vm.planInWarrantyPeriod) {
      <p class="body mt-12">Your {{ vm.product }} is protected against accidental damage.</p>
      <p class="body">You also have breakdown cover under the {{ vm.manufacturer }} guarantee.</p>
    } @else {
      <p class="body mt-12">Your {{ vm.product }} is protected against {{ vm.claimTypesMessage }}.</p>
    }
  } @else {
    <h2 class="heading-4" data-testid="repair-heading">Need a repair?</h2>
    @if (vm.isAD) {
      <p class="body mt-12">You have no repair booked.</p>
    } @else if (vm.isBD) {
      <p class="body mt-12">
        You’re covered for breakdowns. For more details, please
        <a (click)="onDownloadPlanDocs()()" class="link-text" role="button" tabindex="0">read your policy documents.</a>
      </p>
    }
  }
}

@if (vm.claimType === 'service') {
  <h2 class="heading-4" data-testid="service-heading">Your annual service</h2>
  <p class="body mt-12">Your plan includes one service per year.</p>
}

@for (step of vm.stepsToFlowThrough | slice: 0 : currentStep(); track step) {
  @let currentStepName = vm.stepsToFlowThrough[currentStep() - 1];

  @switch (step) {
    @case ('claimType') {
      @if (vm.claimOptions.length === 1 && currentStepName === 'claimType') {
        <div class="divider"></div>
        <section class="single-repair-button flex mt-20">
          <app-button
            [dataLabel]="'book-a-' + vm.claimType"
            (onClick)="claimTypeSelected(vm.claimOptions[0].claimTypeCode, currentStepName)"
          >
            Book a {{ vm.claimType }}
          </app-button>
        </section>
      } @else if (vm.claimOptions.length > 1) {
        @if (!isWhiteLabel) {
          <section class="claim-options gap-12 mt-20 flex">
            @for (option of vm.claimOptions; track option.claimTypeCode) {
              @let mapping = claimTypeMapping[option.claimTypeCode];
              <label [for]="option.claimTypeCode">
                <input
                  type="radio"
                  [id]="option.claimTypeCode"
                  [value]="option.claimTypeCode"
                  [ngModel]="selectedClaimTypeCode()"
                  (ngModelChange)="claimTypeSelected($event, currentStepName)"
                />
                <img
                  [hidden]="option.claimTypeCode === selectedClaimTypeCode()"
                  [src]="'/assets/images/claim-options/' + mapping.image + '_default.svg'"
                  alt=""
                />

                <img
                  [hidden]="option.claimTypeCode !== selectedClaimTypeCode()"
                  [src]="'/assets/images/claim-options/' + mapping.image + '_fill.svg'"
                  alt=""
                  class="active"
                />
                <p class="body mt-12">
                  {{ mapping.label }}
                </p>
              </label>
            }
          </section>
        } @else {
          <section class="claim-options gap-12 mt-20 flex">
            @for (option of vm.claimOptions; track option.claimTypeCode) {
              @let mapping = claimTypeMapping[option.claimTypeCode];
              <app-button
                class="claim-option-btn"
                [buttonOptions]="{
                  variant: 'secondary',
                  fill: true,
                  pressed: selectedClaimTypeCode() === option.claimTypeCode,
                }"
                [dataLabel]="mapping.label"
                (onClick)="claimTypeSelected(option.claimTypeCode, currentStepName)"
              >
                {{ mapping.label }}
              </app-button>
            }
          </section>
        }

        @if (vm.planInWarrantyPeriod && selectedClaimTypeCode() === 'BD') {
          <p class="body mt-16">To book your repair under the {{ vm.manufacturer }} guarantee, please call:</p>
          <app-telephone-link
            class="mt-8"
            postCodeMessagingClasses="max-w-480"
            [phoneNumber]="vm.phoneNumber"
            [largeFont]="true"
            [postCode]="vm.postCode"
          />
        }
      }
    }

    @case ('manufacturer') {
      <section class="manufactuer mt-24 flex flex-col gap-8">
        <p class="label">Let us know who made your appliance</p>

        <p class="hint">This helps us select the right service provider for your repair.</p>
        <button (click)="currentStepName === 'manufacturer' ? currentStep.set(currentStep() + 1) : null" type="button">
          Move on from this
        </button>
      </section>
    }

    @case ('productType') {
      <section class="product-type mt-24 flex flex-col gap-8" data-testid="product-type-question">
        <p class="label">{{ productTypeQuestionTitle }}</p>
        <section class="flex gap-8 mt-12" style="flex-wrap: unset">
          @for (
            option of vm.isTV ? sortScreenSizes(vm.dataLists.products) : vm.dataLists.products;
            track option;
            let index = $index
          ) {
            <app-radio-button
              class="full-width max-w-228"
              variant="blue"
              name="product-type"
              [(ngModel)]="selectedProductType"
              [index]="index"
              [optionValue]="option"
              (ngModelChange)="productTypeSelected(option)"
            >
              {{ formatOption(option) }}
            </app-radio-button>
          }
        </section>
      </section>
    }

    @case ('gasSafety') {
      <section class="gas-safety mt-24 flex flex-col gap-8" data-testid="gas-safety-question">
        <p class="label">Do you smell gas or think there’s a carbon monoxide leak?</p>
        <section class="flex gap-8 mt-12" style="flex-wrap: unset">
          @for (option of ['Yes', 'No']; track option; let index = $index) {
            <app-radio-button
              class="full-width max-w-228"
              variant="blue"
              name="gas-safety"
              [(ngModel)]="gasSafetyAnswer"
              [index]="index"
              [optionValue]="option"
              (ngModelChange)="gasSafetyAnswer === 'No' ? moveOnFromGasSafety() : moveBackToGasASafety()"
            >
              {{ option }}
            </app-radio-button>
          }
        </section>
      </section>

      @if (gasSafetyAnswer === 'Yes') {
        <p class="label mt-16">What are you reporting?</p>
        <section class="flex flex-col gap-8 mt-12" style="flex-wrap: unset">
          @for (
            option of ['Gas smell after a Domestic & General repair', 'Gas leak', 'Carbon monoxide'];
            track option;
            let index = $index
          ) {
            <app-radio-button
              [(ngModel)]="gasSafetyType"
              name="gas-safety-type"
              class="full-width"
              variant="blue"
              [index]="index"
              [optionValue]="option"
            >
              {{ option }}
            </app-radio-button>
          }
        </section>
      }

      @if (gasSafetyType) {
        <ng-container [ngTemplateOutlet]="gasEmergency" />
      }
    }

    @case ('serviceGasLeak') {
      <section class="mt-24 flex flex-col gap-8" data-testid="service-gas-leak-question">
        <p class="label">Do you smell gas or think there's a carbon monoxide leak?</p>
        <section class="flex gap-8 mt-12" style="flex-wrap: unset">
          @for (option of ['Yes', 'No']; track option; let index = $index) {
            <app-radio-button
              class="full-width max-w-228"
              variant="blue"
              name="service-gas-leak"
              [(ngModel)]="serviceGasLeakAnswer"
              [index]="index"
              [optionValue]="option"
              (ngModelChange)="onServiceGasLeakChange()"
            >
              {{ option }}
            </app-radio-button>
          }
        </section>
      </section>

      @if (serviceGasLeakAnswer === 'Yes') {
        <ng-container [ngTemplateOutlet]="gasEmergency" />
      }
    }

    @case ('boilerCondition') {
      <section class="mt-24 flex flex-col gap-8" data-testid="boiler-condition-question">
        <p class="label bold">Is your boiler in good working condition?</p>
        <section class="flex gap-8 mt-12" style="flex-wrap: unset">
          @for (option of ['Yes', 'No']; track option; let index = $index) {
            <app-radio-button
              class="full-width max-w-228"
              variant="blue"
              name="boiler-condition"
              [(ngModel)]="boilerConditionAnswer"
              [index]="index"
              [optionValue]="option"
              (ngModelChange)="onBoilerConditionChange()"
            >
              {{ option }}
            </app-radio-button>
          }
        </section>
      </section>

      @if (boilerConditionAnswer === 'No') {
        <section class="warning mt-16" data-testid="boiler-condition-warning">
          <p class="body">If your boiler is not in good working condition please book a repair.</p>
        </section>
      }
    }

    @case ('baxiLetter') {
      <section class="mt-24 flex flex-col gap-8" data-testid="baxi-letter-question">
        <p class="label bold baxi-letter-question__label">
          Have you received an email, letter, or text message from Baxi in the last 45 days inviting you to book your
          annual service?
        </p>
        <section class="flex gap-8 mt-12" style="flex-wrap: unset">
          @for (option of ['Yes', 'No']; track option; let index = $index) {
            <app-radio-button
              class="full-width max-w-228"
              variant="blue"
              name="baxi-letter"
              [(ngModel)]="baxiLetterAnswer"
              [index]="index"
              [optionValue]="option"
              (ngModelChange)="onBaxiLetterChange()"
            >
              {{ option }}
            </app-radio-button>
          }
        </section>
      </section>

      @if (baxiLetterAnswer === 'Yes') {
        <section class="baxi-notice mt-12" data-testid="baxi-letter-confirmed-notice">
          <i class="fa-light fa-circle-info baxi-notice__icon"></i>
          <section class="flex flex-col gap-4">
            <p class="body bold">If you haven’t heard from Baxi, your booking might not go through</p>
            <p class="body">Baxi should contact you within 45 days of your renewal date to arrange a service.</p>
          </section>
        </section>
      }

      @if (baxiLetterAnswer === 'No') {
        <section class="baxi-call-panel mt-16" data-testid="baxi-letter-call-panel">
          <p class="body bold">If you haven’t heard from Baxi</p>
          <p class="body mt-8">
            If your plan is due to renew, Baxi should contact you within 45 days of your renewal date to arrange a
            service. If you don’t hear from them after this time, please get in touch with their team. Call Baxi on
          </p>
          <app-telephone-link class="mt-8" [phoneNumber]="vm.baxiPhoneNumber" [largeFont]="true" />
          @if (callCostMessaging()) {
            <p class="hint mt-8">{{ callCostMessaging() }}</p>
          }
        </section>
      }
    }

    @case ('address') {
      <section class="address mt-16 flex flex-col gap-8" data-testid="address-confirmation">
        <p class="label">Is this your address?</p>

        <section class="address-section">
          @for (line of vm.address; track line) {
            <p class="body">{{ line }}</p>
          }
        </section>

        <p class="body">
          We'll use this for your {{ vm.claimType | lowercase }}. Check it's correct as you can't change it later.
        </p>

        <section class="choices flex gap-8 mt-12">
          @for (choice of ['yes', 'no']; track choice; let index = $index) {
            <app-radio-button
              class="full-width max-w-228"
              variant="blue"
              [(ngModel)]="addressCorrect"
              [index]="index"
              [optionValue]="choice"
            >
              {{ choice === 'yes' ? 'Yes, it is' : "No, it isn't" }}
            </app-radio-button>
          }
        </section>
      </section>

      @if (addressCorrect) {
        <section class="address-info" data-testid="address-info">
          <div class="divider"></div>
          @if (addressCorrect === 'no') {
            <section class="warning flex gap-8 full-width" data-testid="address-warning">
              <section class="content flex flex-col gap-4">
                <p class="body bold">
                  <i class="fa-light fa-circle-exclamation"></i>
                  Update your address
                </p>
                <p class="body">You can do this in Your Details.</p>
              </section>

              <section class="link">
                <app-button
                  dataLabel="update-your-details"
                  [buttonOptions]="{ variant: 'link', isLink: true, url: vm.yourDetailsPageLink }"
                >
                  <span>Your details</span>
                  <i class="fa-light fa-arrow-right ml-8"></i>
                </app-button>
              </section>
            </section>
          } @else {
            <app-button
              (onClick)="createBooking()"
              [attr.data-testid]="'book-a-' + vm.claimType"
              dataLabel="book a repair"
            >
              Book a {{ vm.claimType }}
              <i class="fa-light fa-arrow-right ml-4"></i>
            </app-button>
          }
        </section>
      }
    }
  }
}

<ng-template #gasEmergency>
  <div class="divider"></div>
  <section class="gas-safety-type" data-testid="gas-safety-type">
    <h2 class="heading-4 center">Call the National Gas Emergency Service</h2>

    <section class="warning">
      <p class="body bold">
        We can't book a {{ vm.claimType | lowercase }} if there's a suspected gas or carbon monoxide leak.
      </p>
    </section>

    <section>
      <p class="body center">If you smell gas or have any concerns, call the National Gas Emergency Service.</p>

      <p class="body center">It’s free and open any time. They’ll check your supply and make sure everything’s safe.</p>
    </section>

    <section class="numbers">
      <section class="area">
        <p class="body bold">Northern Ireland</p>

        <app-telephone-link [largeFont]="true" phoneNumber="0800 022 001" />
      </section>

      <section class="area">
        <p class="body bold">England, Wales and Scotland</p>

        <app-telephone-link [largeFont]="true" phoneNumber="0800 111 999" />
      </section>

      <section class="area">
        <p class="body bold">Isle of Man</p>

        <app-telephone-link [largeFont]="true" phoneNumber="0808 1624 444" />
      </section>
    </section>

    <p class="body">
      Or visit
      <a class="link-text" href="https://www.nationalgas.com/emergency-contacts" target="_blank">www.nationalgas.com</a>
    </p>
  </section>
</ng-template>
